import React, { useEffect, useState } from "react";
import { Button, Form, Input } from "antd";
import { Link, useParams } from "react-router-dom";
import { useResetPassword } from "../../../../../services/auth/mutations";
import { EyeOutlined, EyeInvisibleOutlined, ArrowLeftOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";

interface Props {
  onReady: Function;
  handlerRedirectSignIn?: any;
}

interface PasswordRequirement {
  label: string;
  validator: (password: string) => boolean;
  met: boolean;
}

const FormResetPassword = ({ onReady, handlerRedirectSignIn }: Props) => {

  let { token }: any = useParams();

  const { data: response, execute: resetPassword, fetching } = useResetPassword();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasSubmittedWithErrors, setHasSubmittedWithErrors] = useState(false);

  useEffect(() => {
    if (response) onReady();
  }, [response])

  const passwordRequirements: PasswordRequirement[] = [
    {
      label: "Lowercase letter",
      validator: (pwd: string) => /[a-z]/.test(pwd),
      met: false
    },
    {
      label: "Uppercase letter",
      validator: (pwd: string) => /[A-Z]/.test(pwd),
      met: false
    },
    {
      label: "Symbol",
      validator: (pwd: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`§±€£¥¢¤¦¨©®™°²³¹¼½¾×÷≠≈≤≥∞∑∏√∫∂∆∇∈∉∋∌∩∪⊂⊃⊆⊇⊕⊗∀∃∄∴∵∝∞∅∆∇∈∉∋∌∩∪⊂⊃⊆⊇⊕⊗∀∃∄∴∵∝∞∅]/.test(pwd),
      met: false
    },
    {
      label: "8 characters",
      validator: (pwd: string) => pwd.length >= 8,
      met: false
    }
  ];

  const getPasswordRequirements = () => {
    return passwordRequirements.map(req => ({
      ...req,
      met: req.validator(password)
    }));
  };

  const onFinish = (values: any) => {
    const currentRequirements = getPasswordRequirements();
    const allRequirementsMet = currentRequirements.every(req => req.met);
    
    if (!allRequirementsMet) {
      setHasSubmittedWithErrors(true);
      return;
    }
    
    setHasSubmittedWithErrors(false);
    resetPassword({ newPassword: values.password, token });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const currentRequirements = getPasswordRequirements();

  return (
    <Form
      name="basic"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      className="form"
      autoComplete="off">

      <div className="form-field-group">
        <label className="form-label">Password</label>
        <Form.Item
          name="password"
          className="form-item password-item"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              required: true,
              message: "Please input your password!",
            },
          ]}>
          <div className="password-input-container">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder=""
              className="form-item__input password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          </div>
        </Form.Item>

        <div className="password-requirements">
          {currentRequirements.map((req, index) => (
            <div key={index} className={`requirement ${req.met ? 'met' : 'not-met'} ${hasSubmittedWithErrors && !req.met ? 'error-state shake-animation' : ''}`}>
              <span className="requirement-icon">
                {req.met ? <CheckOutlined size={13} /> : <CloseOutlined size={13} />}
              </span>
              <span className="requirement-text">{req.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="form-field-group">
        <label className="form-label">Confirm password</label>
        <Form.Item
          name="confirmPassword"
          className="form-item confirm-password-item"
          validateTrigger={['onChange', 'onBlur']}
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: 'Please confirm your password!',
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('The new password that you entered do not match!'));
              },
            }),
          ]}>
          <div className="password-input-container">
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder=""
              className="form-item__input password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
          </div>
        </Form.Item>
      </div>

      <Form.Item>
        <Button
          className="button button-primary button-txt-big reset-password-btn"
          htmlType="submit"
          loading={fetching}
        >
          Reset password
        </Button>
      </Form.Item>

      <div className="back-to-login">
        <Link to="/login" className="back-link">
          <ArrowLeftOutlined className="arrow-icon" />
          Back to login
        </Link>
      </div>
    </Form>
  );
};
export default FormResetPassword;
